/**
 * Normalise an API failure into an Error with a useful message.
 *
 * Replaces the `catch(({ response }) => { throw new Error(response.data.message) })`
 * pattern, which was repeated 37 times across src/utils/api and assumed every
 * failure carries a response.
 *
 * That assumption used to be true by accident: the service worker answered an
 * unreachable `/api/` request with a synthesized `500` that had a body, so the
 * destructure produced a (useless) `Error: undefined`. Now that the worker returns
 * `Response.error()` — which is what makes offline fallbacks fire at all — there is
 * no `response`, and `response.data` throws a TypeError from inside the `.catch`,
 * escaping callers that expect an Error.
 *
 * The original `request`, `response` and `code` are carried onto the new Error so
 * `isNetworkFailure` (src/utils/offlineFallback.ts) still classifies it correctly
 * after conversion, and `cause` keeps the original for debugging.
 */
export const apiError = (err: any): Error => {
  const serverMessage = err?.response?.data?.message;

  const message =
    serverMessage ||
    (err?.response
      ? err.message || 'The server rejected this request.'
      : 'Unable to reach the server. Check your connection and try again.');

  const normalised: any = new Error(message);

  normalised.request = err?.request;
  normalised.response = err?.response;
  normalised.code = err?.code;
  normalised.cause = err;

  return normalised;
};

export default apiError;
